import React, { useState, useEffect, ReactNode } from "react";
import { useTranslation, useI18next } from "gatsby-plugin-react-i18next";
import { RangePicker } from "components/DatePicker/RangePicker/RangePicker";
import InputFloatLabel from "components/Design/Input/InputFloatLabel";
import InputDateRangePicker from "components/DateRangeInput/InputDateRangePicker";
import dayjs from "utils/dayjs";
import type { Dayjs } from "dayjs";
import useDrawer from "hooks/useDrawer";
import CommonDrawer from "components/Design/Drawer/Drawer";
import DrawerHead from "components/Design/Drawer/DrawerHead";
import {
  DATEPICKER_DATE_FORMAT,
  APP_DATE_WITH_DAYNAME_FORMAT,
  ISO_FORMAT,
} from "store/constants/date";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { type DateRateFomatted } from "apis/calendarRates";

dayjs.extend(customParseFormat);

type InputDateRangeDrawerProps = {
  startDate: Dayjs | string;
  endDate: Dayjs | string;
  onChange: ({
    startDate,
    endDate,
  }: {
    startDate: Dayjs | string;
    endDate: Dayjs | string;
  }) => void;
  onSave: (dates: {
    startDate: Dayjs | string;
    endDate: Dayjs | string;
  }) => void;
  drawerProps: {
    placement: string;
    height: string;
  };
  saveCta: (onClick: () => void) => ReactNode;
  title: string;
  fullBorderInput: boolean;
  format?: (lang: string) => string;
  withIcon: boolean;
  dateRate: Record<string, DateRateFomatted> | null;
  note?: string;
};

export const InputDateRangeDrawer = ({
  startDate,
  endDate,
  onChange,
  onSave,
  drawerProps,
  saveCta,
  title,
  fullBorderInput,
  format,
  withIcon,
  dateRate,
  note,
}: InputDateRangeDrawerProps) => {
  const { t } = useTranslation("homepage");
  // @ts-ignore
  const { language }: { language: "en" | "ja" | "zh" | "ko" } = useI18next();
  dayjs.locale(language === "zh" ? "zh-cn" : language);
  const finalFormat = format?.(language) || DATEPICKER_DATE_FORMAT[language];

  const [focusInput, setFocusInput] = useState<"startDate" | "endDate">(
    "startDate"
  );
  const { reset, open, isOpen } = useDrawer();
  const [startDateValue, setStartDateValue] = useState(
    dayjs(startDate).format(finalFormat)
  );
  const [endDateValue, setEndDateValue] = useState(
    dayjs(endDate).format(finalFormat)
  );

  const onStartDateClick = () => {
    setFocusInput("startDate");
    open();
  };

  const onEndDateClick = () => {
    setFocusInput("endDate");
    open();
  };

  const onDatesChange = (dates: {
    startDate: Dayjs | string;
    endDate: Dayjs | string;
  }) => {
    onChange({
      startDate: dayjs(dates.startDate),
      endDate: dayjs(dates.endDate),
    });
    onSave(dates);
  };

  // When only startdate is selected and drawer is closed / date is saved
  // set endDate = startDate +1 and set the new dates(startDate & endDate) in the calendar
  useEffect(() => {
    if (!Boolean(endDateValue) && !isOpen) {
      if (language === "en" && format) {
        setEndDateValue(
          dayjs(startDateValue)
            .add(1, "day")
            .format(APP_DATE_WITH_DAYNAME_FORMAT)
        );
      } else {
        setEndDateValue(
          dayjs(startDateValue, finalFormat, language)
            .add(1, "day")
            .format(finalFormat)
        );
      }
    }
    const newDates = {
      startDate:
        language === "en"
          ? dayjs(startDateValue).format(ISO_FORMAT)
          : dayjs(startDateValue, ISO_FORMAT, "en").format(ISO_FORMAT),
      endDate:
        language === "en"
          ? dayjs(endDateValue).format(ISO_FORMAT)
          : dayjs(endDateValue, ISO_FORMAT, "en").format(ISO_FORMAT),
    };
    onSave(newDates);
  }, [endDateValue, startDateValue, isOpen, language, format]);

  return (
    <div className="w-full h-full relative">
      <InputDateRangePicker
        startDateLabel={t("Check-in")}
        startDateValue={startDateValue}
        startDateClick={onStartDateClick}
        endDateValue={endDateValue}
        endDateLabel={t("Check-out")}
        endDateClick={onEndDateClick}
        fullBorder={fullBorderInput}
        withIcon={withIcon}
      />

      <CommonDrawer
        className="date-input-drawer"
        data-testid="date-drawer"
        destroyOnClose
        visible={isOpen}
        onClose={reset}
        placement={drawerProps.placement}
        height={drawerProps.height}
        width="100%">
        <DrawerHead title={title} onClose={reset} />

        <div className="grid grid-cols-2 shadow-input-booking my-12 mx-auto max-w-342">
          <div className="border border-r-0 border-gray-light pointer-events-none">
            {/* TODO: Remove after converting this to TS */}
            {/* @ts-ignore */}
            <InputFloatLabel
              label={t("Check-in")}
              value={startDateValue}
              noBorder
              readOnly
              data-testid="check-in-from-drawer"
              name={t("Check-in")}
            />
          </div>
          <div className="border md:border-r border-gray-light pointer-events-none">
            {/* TODO: Remove after converting this to TS */}
            {/* @ts-ignore */}
            <InputFloatLabel
              label={t("Check-out")}
              value={endDateValue}
              noBorder
              readOnly
              data-testid="check-out-from-drawer"
              name={t("Check-out")}
            />
          </div>
        </div>
        {note && (
          <p className="my-20 text-12 font-mulish text-center">{note}</p>
        )}
        {isOpen && (
          <RangePicker
            focusInput={focusInput}
            startDate={dayjs(startDate)}
            endDate={dayjs(endDate)}
            onChange={onDatesChange}
            orientation="vertical"
            setStartDateValue={setStartDateValue}
            setEndDateValue={setEndDateValue}
            format={format}
            dateRate={dateRate}
            note={note}
          />
        )}
        {isOpen && saveCta(() => reset())}
      </CommonDrawer>
    </div>
  );
};
